<script setup lang="ts">
// NPM
import { ref, computed, reactive, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMq } from 'vue3-mq';
import { isAxiosError } from 'axios';

// Components
import {
  BaseAvatar,
  BaseButton,
  BaseIcon,
  PopoverMenu,
  getDisplayName,
  getInitials
} from '@magma-app/magma-lapilli';
import RelationContactBlock from '@/components/relations/RelationContactBlock.vue';
import RelationParties from '@/components/relations/RelationParties.vue';
import RelationDisclosure from '@/components/relations/RelationDisclosure.vue';
import RelationFeedback from '@/components/relations/RelationFeedback.vue';
import RelationStatePanel from '@/components/relations/RelationStatePanel.vue';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Composables & Stores
import { useDeterminePunctuation } from '@/composables/useDeterminePunctuation';
import { useRelationState } from '@/composables/useRelationState';
import { useCommonStore } from '@/stores/common';
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
import type { UserDashboardRelationsGetHelperRelationDetailsResponseType } from '@magma-app/magma-types/all';

const { determinePunctuation } = useDeterminePunctuation();
const relationsStore = useRelationsStore();
const commonStore = useCommonStore();
const userStore = useUserStore();

// Plugins
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n({ useScope: 'global' });
const mq = useMq();

// Reactive variables
const initialized = ref(false);
const relation =
  ref<UserDashboardRelationsGetHelperRelationDetailsResponseType | null>(null);
const isHandlingOngoingQuery = ref(false);
let unavailableMomentHandled = false;

// Relation state — single source of truth, shared with the action bar and the badge
const { state, isTerminal, canContactAdmin, primaryActionKey } =
  useRelationState(relation);

// Computed properties
const relationId = computed(() =>
  route?.params.relationId
    ? parseInt(route?.params.relationId as string)
    : undefined
);

// Modal submissions refresh the store. Adopt only the selected relation so late requests
// from a previous selection cannot replace the current detail.
watch(
  () => relationsStore.relationHelper,
  (value) => {
    if (value?.relation.relationMoment.relationId === relationId.value)
      relation.value = value;
  },
  { immediate: true }
);

const prospect = computed(() => relation.value?.relation?.prospect);

const prospectFullName = computed(() =>
  prospect.value
    ? getDisplayName(
        prospect.value.firstname,
        prospect.value.lastname,
        prospect.value.email
      )
    : ''
);

const prospectAvatarFallback = computed(() =>
  prospect.value
    ? getInitials(
        prospect.value.firstname,
        prospect.value.lastname,
        prospect.value.email
      )
    : ''
);

const relationMoment = computed(() => relation.value?.relation.relationMoment);

const successful = computed(() =>
  ['accepted', 'exchangeValidated'].includes(state.value)
);
const isRelationActionable = computed(
  () => !isTerminal.value && !relation.value?.relation.metaStatus
);
const canUseContacts = computed(
  () => isRelationActionable.value || successful.value
);
const canSeeProspectFeedback = computed(
  () =>
    !!relationMoment.value?.helper?.validatedAt &&
    relationMoment.value?.prospect?.helpeeRating === 2
);
const helperInitials = computed(() =>
  getInitials(
    userStore.user?.firstname || '',
    userStore.user?.lastname || '',
    userStore.user?.email || ''
  )
);
const sections = reactive({
  campaign: false,
  request: false,
  contact: false,
  message: false
});
const hasRequest = computed(
  () => !!prospect.value?.expectation || !!prospect.value?.criteria?.length
);
const failedProspectAvatar = ref(false);
watch(
  () =>
    [relation.value?.relation.relationMoment.relationId, state.value] as const,
  ([id, current], previous) => {
    if (!id) return;
    const actionable = ['toContact', 'contacted', 'toConfirm'].includes(
      current
    );
    if (id !== previous?.[0]) {
      sections.campaign = false;
      sections.message = false;
      sections.request = actionable;
      sections.contact = actionable;
      failedProspectAvatar.value = false;
    } else if (
      !actionable &&
      ['toContact', 'contacted', 'toConfirm'].includes(previous?.[1] || '')
    ) {
      Object.assign(sections, {
        campaign: false,
        request: false,
        contact: false,
        message: false
      });
    }
  },
  { immediate: true }
);
const copyMessage = async () => {
  try {
    await navigator.clipboard.writeText(prospect.value?.welcomeMessage || '');
    commonStore.setAlert({
      open: true,
      type: 'success',
      title: t('helperRelation.channels.copied'),
      duration: 1800
    });
  } catch {
    commonStore.setAlert({
      open: true,
      type: 'error',
      title: t('helperRelation.channels.copyFailed')
    });
  }
};

const campaignTitle = computed(
  () =>
    userStore.campaigns.find(
      (campaign) => campaign.id === relation.value?.campaignId
    )?.title || ''
);

const reportedLabel = computed(() => {
  const reportedAt = relation.value?.report?.reportedAt;
  if (!relation.value?.isReported || !reportedAt) return '';

  const date = new Date(reportedAt).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  return `${t('helperRelationOverview.info.partOne')} ${date}, ${t(
    'helperRelationOverview.info.partTwo'
  )}`;
});

const menuItems = computed(() =>
  isRelationActionable.value
    ? [
        {
          name: t('helperRelation.report.cta'),
          type: 'danger' as const,
          icon: 'flag',
          onClick: onReport
        }
      ]
    : []
);

const showUnavailableRelation = () => {
  commonStore.setAlert({
    open: true,
    type: 'error',
    title: t('actionDeepLink.notFound.title'),
    description: t('actionDeepLink.notFound.relation'),
    duration: 5000
  });
};

const rejectUnavailableMoment = async () => {
  if (!('moment' in route.query) || unavailableMomentHandled) return false;

  const requestedMomentId = Number(
    Array.isArray(route.query.moment)
      ? route.query.moment[0]
      : route.query.moment
  );
  const currentMomentId = relationMoment.value?.relationMomentId;
  if (
    Number.isInteger(requestedMomentId) &&
    requestedMomentId > 0 &&
    requestedMomentId === currentMomentId
  ) {
    return false;
  }

  unavailableMomentHandled = true;
  showUnavailableRelation();
  const query = { ...route.query };
  delete query.moment;
  delete query.helper;
  await router.replace({ path: route.path, query });
  return true;
};

// Helper functions
const handleContactQuery = () => {
  if (!isRelationActionable.value) return;

  if (!relationsStore.relationHelper?.hasBeenInContact) {
    router.push({
      name: 'start-relation-helper',
      params: { relationId: route.params.relationId }
    });
  }
};

const refreshRelation = async () => {
  if (!relationId.value) return;

  await relationsStore.getRelation({
    relationId: relationId.value,
    type: 'helper'
  });
};

const handleOngoingQuery = async () => {
  if (!relation.value || isHandlingOngoingQuery.value) return;
  if (!isRelationActionable.value) return;

  const queryMomentId = Number(route?.query.moment);
  const moment = relationMoment.value;

  if (!moment || Number.isNaN(queryMomentId)) return;
  if (moment.relationMomentId !== queryMomentId) return;

  if (!moment.helper?.isOngoing) {
    isHandlingOngoingQuery.value = true;

    try {
      await relationsStore.markRelationMomentAs({
        relationMomentId: moment.relationMomentId,
        role: 'ongoing',
        type: 'helper'
      });

      await refreshRelation();
    } finally {
      isHandlingOngoingQuery.value = false;
    }
  }
};

const openConfirmModal = () => {
  const moment = relationMoment.value;
  if (!moment || !relation.value) return;

  commonStore.setFeedbackRelationModal({
    description:
      t('generic.doneModalDescription') +
      prospect.value?.firstname +
      t('generic.doneModalDescriptionBis'),
    open: true,
    title: t('generic.doneModalTitle'),
    relationMomentId: moment.relationMomentId,
    role: 'done-without-feedback',
    type: 'helper',
    persona: 'helper',
    needFeedbackRating: moment.helper?.needFeedbackRating,
    needFeedbackComment: moment.helper?.needFeedbackComment,
    needFeedbackFile: moment.helper?.needFeedbackFile,
    instructionFeedback: moment.description || ''
  });
};

const handleDoneQuery = () => {
  if (!relation.value) return;
  if (!isRelationActionable.value) return;

  const queryMomentId = Number(route?.query.moment);
  const moment = relationMoment.value;

  if (!moment || Number.isNaN(queryMomentId)) return;
  if (moment.relationMomentId !== queryMomentId) return;
  if (moment.helper?.isValidated || moment.status !== 'ongoing') return;

  openConfirmModal();
};

const handleFeedbackQuery = () => {
  if (!relation.value) return;

  commonStore.setFeedbackRelationModal({
    open: true,
    type: 'helper',
    persona: 'helper',
    role: 'done-with-feedback',
    feedbackType: 'full',
    relationMomentId: Number(route?.query.moment),
    title: t('generic.doneModalTitle'),
    description: `${t('generic.feedbackModalDescription')}${prospect.value
      ?.firstname}.`,
    helpeeName: prospect.value?.firstname
  });
};

const openAdminModal = () => {
  commonStore.setAdminModal({
    relationMomentId: relationMoment.value?.relationMomentId,
    open: true
  });

  if (mq.smMinus) {
    document.body.classList.add('overflow-hidden');
  }
};

const handleAdminQuery = () => {
  commonStore.setAdminModal({
    relationMomentId: parseInt(route?.query.moment as string),
    open: true
  });

  if (mq.smMinus) {
    document.body.classList.add('overflow-hidden');
  }
};

// Watchers - separated by concern
watch(
  relationId,
  async (id) => {
    initialized.value = false;
    relation.value = null;
    unavailableMomentHandled = false;
    if (!id) {
      if ('relationId' in route.params) {
        showUnavailableRelation();
        await router.replace({ name: 'relations-empty-state' });
      }
      return;
    }
    try {
      await refreshRelation();
      if (id !== relationId.value) return;
      await userStore.getCampaignInfoHelper(id);
    } catch (error) {
      if (id !== relationId.value) return;
      console.error('Failed to fetch relation or campaign info:', error);
      if (
        isAxiosError(error) &&
        [403, 404].includes(error.response?.status || 0)
      ) {
        showUnavailableRelation();
        await router.replace({ name: 'relations-empty-state' });
      }
    } finally {
      if (id === relationId.value) initialized.value = true;
    }
  },
  { immediate: true }
);

// Handle query-based actions
watchEffect(async () => {
  if (!initialized.value || !relation.value || !route.query.helper) return;
  if (await rejectUnavailableMoment()) return;

  switch (route.query.helper) {
    case 'contact':
      handleContactQuery();
      break;
    case 'read':
      // The page now shows everything at once, so landing on it is the whole behaviour.
      break;
    case 'ongoing':
      handleOngoingQuery();
      break;
    case 'done':
      handleDoneQuery();
      break;
    case 'feedback':
      handleFeedbackQuery();
      break;
    case 'admin':
      handleAdminQuery();
      break;
  }
});

// Event listeners
const onGoBackToRelations = () => {
  router.push({ name: 'relations' });
};

function onReport() {
  if (!isRelationActionable.value) return;

  commonStore.setReportModal({
    open: true,
    role: 'report',
    type: 'helper',
    title: t('generic.reportTitle'),
    description: t('generic.reportDescription'),
    helpeeName: prospect.value?.firstname
  });
}

const onPrimaryAction = () => {
  if (primaryActionKey.value) openConfirmModal();
};
</script>

<template>
  <div
    v-if="initialized && relation"
    class="flex h-full w-full min-w-0 flex-col"
    data-cy="helper-relation"
  >
    <header class="flex items-center gap-3 px-4 py-4 md:px-6 lg:px-8">
      <button
        type="button"
        class="flex h-11 w-8 shrink-0 items-center justify-center lg:hidden"
        :aria-label="$t('generic.back')"
        @click="onGoBackToRelations"
      >
        <BaseIcon icon="chevron-left" />
      </button>
      <div class="shrink-0" @error.capture="failedProspectAvatar = true">
        <BaseAvatar
          :src="failedProspectAvatar ? null : prospect?.profilePictureUrl"
          :fallback="prospectAvatarFallback"
          :alt="prospectFullName"
          size="lg"
        />
      </div>
      <div class="min-w-0 flex-1">
        <h1
          :title="prospectFullName"
          class="line-clamp-2 break-words text-mgm-txt-lg sm:text-mgm-txt-xl font-semibold text-foreground-emphasis"
        >
          {{ prospectFullName }}
        </h1>
        <p
          v-if="campaignTitle"
          class="mt-1 text-mgm-txt-xs text-foreground-subtle"
        >
          {{ campaignTitle }}
        </p>
      </div>
      <BaseButton
        v-if="isRelationActionable"
        class="hidden shrink-0 lg:inline-flex"
        state="secondary"
        icon="flag"
        data-cy="report-relation"
        @click="onReport"
        >{{ $t('helperRelation.report.cta') }}</BaseButton
      >
      <div v-if="menuItems.length" class="lg:hidden">
        <PopoverMenu :items="menuItems"
          ><template #trigger
            ><button
              type="button"
              class="flex h-11 w-11 items-center justify-center"
              aria-haspopup="menu"
              :aria-label="$t('helperRelation.report.cta')"
            >
              <BaseIcon icon="more-horizontal" /></button></template
        ></PopoverMenu>
      </div>
    </header>
    <div
      class="min-h-0 flex-1 overflow-y-auto px-4 pb-8 pt-2 md:px-6 lg:px-8"
      data-cy="relation-detail-scroll"
    >
      <div class="flex min-w-0 flex-col gap-6">
        <RelationStatePanel
          :state="state"
          :prospect-firstname="prospect?.firstname || ''"
          :points="relationMoment?.points || 0"
          :reward-points="relation.rewardPoints || 0"
          :meta-status="relation.relation.metaStatus"
          :reviewed-by="relationMoment?.reviewedBy"
          :has-positive-feedback="relationMoment?.prospect?.hasPositiveFeedback"
        >
          <BaseButton
            v-if="primaryActionKey"
            class="hidden shrink-0 lg:inline-flex"
            state="primary"
            data-cy="relation-primary-action-desktop"
            @click="onPrimaryAction"
            >{{ $t(primaryActionKey) }}</BaseButton
          >
        </RelationStatePanel>
        <p v-if="reportedLabel" class="text-mgm-txt-xs text-foreground-subtle">
          {{ reportedLabel }}
        </p>
        <RelationParties
          :state="state"
          :prospect-firstname="prospect?.firstname || ''"
          :prospect-initials="prospectAvatarFallback"
          :prospect-avatar="prospect?.profilePictureUrl"
          :helper-avatar="userStore.user?.profilePictureUrl"
          :helper-initials="helperInitials"
          :organization-logo="
            userStore.campaignInfo?.logoUrl ||
            userStore.user?.organization?.logoUrl
          "
          :helper-validated-at="relationMoment?.helper?.validatedAt"
          :prospect-validated-at="relationMoment?.prospect?.validatedAt"
          :reviewed-at="relationMoment?.reviewedAt"
          :reviewed-by="relationMoment?.reviewedBy"
        />
        <RelationFeedback
          v-if="canSeeProspectFeedback"
          :name="prospect?.firstname || ''"
          :comment="relationMoment?.prospect?.helpeeComment"
        />
        <button
          v-if="canContactAdmin"
          type="button"
          class="flex min-h-11 w-fit items-center gap-2 text-mgm-txt-xs text-foreground-brand-default"
          data-cy="contact-admin"
          @click="openAdminModal"
        >
          <BaseIcon icon="flag" :size="18" />{{
            $t('helperRelation.blocked.cta')
          }}
        </button>
        <div
          class="min-w-0 divide-y divide-border-subtle"
          data-cy="relation-sections"
        >
          <RelationDisclosure
            v-if="relation.relation.description"
            v-model="sections.campaign"
            :title="$t('helperRelation.campaign.title')"
            icon="file-text"
            data-cy="campaign-description"
          >
            <div
              class="rich-text-content break-words text-mgm-txt-sm text-foreground-default"
              v-html="relation.relation.description"
            ></div>
          </RelationDisclosure>
          <RelationDisclosure
            v-if="hasRequest"
            v-model="sections.request"
            :title="$t('helperRelation.request.title')"
            icon="message-circle"
            data-cy="relation-request"
          >
            <div
              v-if="prospect?.expectation"
              class="max-w-3xl break-words rounded-2xl rounded-tl-sm bg-background-subtle p-4 text-mgm-txt-sm text-foreground-emphasis"
            >
              <p v-if="prospect.firstname" class="mb-1 font-medium">
                {{ prospect.firstname }} :
              </p>
              <p class="whitespace-pre-wrap">{{ prospect.expectation }}</p>
            </div>
            <dl
              v-if="prospect?.criteria?.length"
              class="flex flex-col gap-5"
              :class="{ 'mt-5': prospect?.expectation }"
            >
              <div
                v-for="(criterium, index) in prospect.criteria"
                :key="index"
                class="break-words text-mgm-txt-sm"
              >
                <dt class="font-medium text-foreground-emphasis">
                  {{
                    determinePunctuation(criterium.labelHelpee || '', ':', [
                      '?'
                    ])
                  }}
                </dt>
                <dd class="mt-1 whitespace-pre-wrap text-foreground-default">
                  {{ criterium.value }}
                </dd>
              </div>
            </dl>
          </RelationDisclosure>
          <div
            v-if="prospect?.touchPoints?.length"
            data-cy="relation-contact-block"
          >
            <RelationDisclosure
              v-model="sections.contact"
              :title="$t('helperRelation.channels.title')"
              icon="mail"
            >
              <RelationContactBlock
                :key="relationMoment?.relationMomentId"
                compact
                hide-message
                :touch-points="prospect.touchPoints"
                :prospect-firstname="prospect.firstname || ''"
                :relation-moment-id="relationMoment?.relationMomentId || 0"
                :is-ongoing="!!relationMoment?.helper?.isOngoing"
                :track-contact="
                  isRelationActionable && !relationMoment?.helper?.validatedAt
                "
                :disabled="!canUseContacts"
                @contacted="refreshRelation"
              />
            </RelationDisclosure>
          </div>
          <RelationDisclosure
            v-if="prospect?.welcomeMessage?.trim()"
            v-model="sections.message"
            :title="$t('helperRelation.channels.suggested')"
            icon="file-text"
            data-cy="relation-message"
          >
            <div class="relative flex max-w-3xl items-start gap-2">
              <p
                class="min-w-0 flex-1 whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm bg-background-brand-default p-4 pr-14 text-mgm-txt-sm sm:pr-4 text-foreground-emphasis"
              >
                {{ prospect.welcomeMessage }}
              </p>
              <button
                type="button"
                class="absolute right-1 top-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-foreground-brand-default sm:static hover:bg-background-subtle-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-foreground-brand-default disabled:opacity-50"
                :aria-label="$t('helperRelation.channels.copy')"
                :title="$t('helperRelation.channels.copy')"
                :disabled="!canUseContacts"
                data-cy="copy-welcome-message"
                @click="copyMessage"
              >
                <BaseIcon icon="copy" :size="20" />
              </button>
            </div>
          </RelationDisclosure>
        </div>
      </div>
    </div>
    <div
      v-if="primaryActionKey"
      class="sticky bottom-0 border-t border-border-subtle bg-background-default px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))] lg:hidden"
    >
      <BaseButton
        size="lg"
        state="primary"
        class="w-full"
        data-cy="relation-primary-action-mobile"
        @click="onPrimaryAction"
        >{{ $t(primaryActionKey) }}</BaseButton
      >
    </div>
  </div>
</template>
